import {
  APPLIED_FILTER_CHIP_FONT,
  APPLIED_FILTER_CHIP_HORIZONTAL_CHROME,
  APPLIED_FILTER_CHIP_LABEL_FONT,
  APPLIED_FILTER_CHIP_MAX_WIDTH,
} from '@constants/appliedFilterChips';

export type TextFits = (text: string) => boolean;

let measuringContext: CanvasRenderingContext2D | null | undefined;

const estimateWidth = (text: string, font: string): number => {
  const size = parseFloat(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '12');

  return text.length * size * 0.6;
};

/**
 * Width of `text` in CSS pixels when set in `font`, measured on one shared
 * off-screen canvas.
 *
 * Falls back to a 0.6em-per-character estimate where no 2D context exists
 * (jsdom), so callers never branch on the environment; tests that need exact
 * widths inject their own measurer.
 */
export const measureTextWidth = (text: string, font: string): number => {
  if (measuringContext === undefined) {
    measuringContext =
      typeof document === 'undefined'
        ? null
        : document.createElement('canvas').getContext('2d');
  }

  if (!measuringContext) {
    return estimateWidth(text, font);
  }

  measuringContext.font = font;

  return measuringContext.measureText(text).width;
};

/**
 * Whether `valueText` fits in the value area an applied-filter chip has left
 * beside `categoryLabel`: the 240px cap, minus the chip's fixed chrome, minus
 * the bold label and its colon. This is the one measurement behind the
 * two-value summary decision — the chip, the root category row and the +N row
 * all read it, so the three texts cannot disagree.
 */
export const chipValueFits = (
  categoryLabel: string,
  valueText: string,
  measure: (text: string, font: string) => number = measureTextWidth,
): boolean => {
  const labelWidth = measure(
    `${categoryLabel}:`,
    APPLIED_FILTER_CHIP_LABEL_FONT,
  );
  const valueArea =
    APPLIED_FILTER_CHIP_MAX_WIDTH -
    APPLIED_FILTER_CHIP_HORIZONTAL_CHROME -
    labelWidth;

  return measure(valueText, APPLIED_FILTER_CHIP_FONT) <= valueArea;
};

/**
 * The selection summary ladder: one value → its name; two → both names when
 * `fits` accepts the joined text, otherwise "2 selected"; three or more →
 * "N selected". Empty input yields an empty string — the "Any" default is the
 * root row's concern, not the ladder's.
 */
export const summarizeSelection = (names: string[], fits: TextFits): string => {
  if (names.length === 0) {
    return '';
  }

  if (names.length === 1) {
    return names[0];
  }

  if (names.length === 2) {
    const joined = names.join(', ');

    return fits(joined) ? joined : '2 selected';
  }

  return `${names.length} selected`;
};
